<script setup lang="ts">
import RoundedButton from "@/components/RoundedButton.vue"
import VbListCustomizeFields from "@/components/crud/VbListCustomizeFields.vue"
import VbListLoginSearchFilter from "@/components/crud/VbListLoginSearchFilter.vue"
import VbListSearchFilterButton from "@/components/crud/VbListLoginSearchFilterButton.vue"
import VbListPagination from "@/components/crud/VbListPagination.vue"
import VbListRefresh from "@/components/crud/VbListRefresh.vue"
import VbListSearch from "@/components/crud/VbListSearch.vue"
import VbListSearchFilter from "@/components/crud/VbListSearchFilter.vue"
import VbListSearchReset from "@/components/crud/VbListSearchReset.vue"
import VbListTable from "@/components/crud/VbListTable.vue"
import VbListToolbar from "@/components/crud/VbListToolbar.vue"
import { useLoginSearchFilter } from "@/composables/useLoginSearchFilter"
import type { LoginSearchFilterConfiguration } from "@/pages/App/Staff/config"
import { makeTableResponsive } from "@/utils/responsive-table"
import { VfList, type InlineTable, type ModuleConfiguration } from "@/vf"
import { computed, inject, onMounted, ref, toRef, toRefs, useTemplateRef, type Ref } from "vue"

const props = withDefaults(
    defineProps<{
        config: ModuleConfiguration
        disableSearch?: boolean
        toolbar?: boolean
        header?: boolean
        pagination?: boolean
        inlineTable?: InlineTable
        searchResetButton?: boolean
        refreshButton?: boolean
        mobileList?: boolean
    }>(),
    {
        disableSearch: false,
        toolbar: true,
        header: true,
        pagination: true,
        searchResetButton: true,
        refreshButton: true,
        mobileList: false,
    },
)

const { config } = toRefs(props)

const list = useTemplateRef("list")

const loginSearchFilterConfiguration = inject<LoginSearchFilterConfiguration | undefined>(
    "login-search-filter-config",
    undefined,
)
const listId = computed(() => loginSearchFilterConfiguration?.id ?? config.value.list?.id())
const loginSearchFilter = loginSearchFilterConfiguration?.enabled ? useLoginSearchFilter() : undefined
loginSearchFilter?.init(listId.value)

defineExpose({
    refresh: () => list.value?.refresh(),
    listState: toRef(() => list.value?.listState),
})

const openedModule: Ref<false | string> = ref(false)
const openModule = {
    open(module: string | false) {
        if (openedModule.value === module) {
            openedModule.value = false
            return
        }
        openedModule.value = module
    },
    close() {
        openedModule.value = false
    },
    isOpen(module: string | undefined) {
        if (!module) {
            return openedModule.value !== false
        }
        return openedModule.value === module
    },
    value() {
        return openedModule.value
    },
}

onMounted(() => {
    if (props.mobileList) {
        makeTableResponsive()
    }
})
</script>
<template>
    <VfList
        :await="true"
        v-bind="{ config }"
        v-slot="{ listState, fields, filteredFieldGroups, moduleAction }"
        ref="list"
    >
        <div
            class="card"
            v-if="listState.list.length > 0 || !$slots.emptyResult"
            :class="{ 'list-mobile': mobileList }"
        >
            <div class="card-header" v-if="header">
                <slot name="header">
                    <div class="page-header">
                        <h1>{{ $t(config.readableName.plural) }}</h1>
                        <h2></h2>
                    </div>
                </slot>
            </div>
            <div class="card-toolbar" v-if="toolbar">
                <slot
                    name="toolbar"
                    v-bind="{
                        listState,
                        filter: listState.filter,
                        refresh: listState.refresh,
                        moduleAction,
                        openModule,
                    }"
                >
                    <VbListToolbar></VbListToolbar>
                    <VbListSearch
                        @search="listState.refresh()"
                        v-if="!disableSearch"
                        class="card-toolbar__item"
                    ></VbListSearch>
                    <VbListSearchFilter
                        :field="field"
                        v-for="field in config.list.filterFields"
                        :key="field.name"
                        class="card-toolbar__item"
                    ></VbListSearchFilter>
                    <RoundedButton
                        icon="fa fa-filter"
                        white
                        @click="openModule.open('loginSearchFilter')"
                        :active="openModule.isOpen('loginSearchFilter')"
                        v-if="loginSearchFilterConfiguration?.enabled"
                        class="card-toolbar__item"
                        :class="{
                            'card-toolbar__item--open': openModule.isOpen('loginSearchFilter'),
                        }"
                    >
                        {{ $t("@app:login.loginSearchFilter") }}
                    </RoundedButton>
                    <slot name="toolbarFilter" v-bind="{ openModule }" />
                    <RoundedButton
                        icon="fa fa-cog"
                        white
                        @click="openModule.open('customizeFields')"
                        :active="openModule.isOpen('customizeFields')"
                        v-if="config.list.fieldsCustomizable"
                        class="card-toolbar__item"
                        :class="{
                            'card-toolbar__item--open': openModule.isOpen('customizeFields'),
                        }"
                    >
                        {{ $t("crud:button.customize") }}
                    </RoundedButton>
                </slot>
                <slot name="toolbarLoginSearchFilter">
                    <div
                        v-if="
                            loginSearchFilterConfiguration?.enabled &&
                            (loginSearchFilter?.getFilters(listId)?.length ?? 0) > 0
                        "
                    >
                        <VbListSearchFilterButton
                            v-bind="{ filter }"
                            v-for="filter in loginSearchFilter?.getFilters(listId)"
                            :key="filter.id"
                            :class="{ 'btn-primary-dark': loginSearchFilter?.isFilterActive(filter) }"
                            class="card-toolbar__item"
                            @click="loginSearchFilter?.loadFilter(filter)"
                        ></VbListSearchFilterButton>
                    </div>
                </slot>
                <slot name="afterToolbar" v-bind="{ listState, filter: listState.filter, openModule }"></slot>
                <VbListRefresh v-if="refreshButton" class="card-toolbar__item"></VbListRefresh>
                <VbListSearchReset
                    v-if="searchResetButton && (Object.keys(listState.filter).length > 0 || listState.searchValue)"
                    class="card-toolbar__item"
                ></VbListSearchReset>
            </div>
            <div class="card-body p-sm-0" :class="{ 'p-0': mobileList }">
                <slot name="openModule" v-bind="{ openModule, filter: listState.filter, refresh: listState.refresh }">
                    <slot
                        name="openModuleFirst"
                        v-bind="{ openModule, filter: listState.filter, refresh: listState.refresh }"
                    ></slot>
                    <div
                        v-animate-show="openModule.isOpen('loginSearchFilter')"
                        v-if="loginSearchFilterConfiguration?.enabled"
                    >
                        <VbListLoginSearchFilter />
                    </div>
                    <div v-animate-show="openModule.isOpen('customizeFields')">
                        <VbListCustomizeFields />
                    </div>
                </slot>
                <slot name="beforeTable" v-bind="{ listState }"></slot>
                <div class="table-responsive position-relative">
                    <VbListTable v-bind="{ config, listState, fields, filteredFieldGroups, inlineTable }">
                        <!-- slot pass through-->
                        <template v-for="(_, name) in $slots" v-slot:[name]="slotData">
                            <slot :name="name" v-bind="slotData" />
                        </template>
                    </VbListTable>
                </div>
                <VbListPagination @page-changed="listState.page = $event" v-if="pagination"></VbListPagination>
                <slot name="afterPagination" v-bind="{ listState }"></slot>
            </div>
        </div>
        <div v-else>
            <slot name="emptyResult"></slot>
        </div>
    </VfList>
</template>
